import { type FC, memo, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type PageContentProps = {
  children: ReactNode;
  className?: string;
  variant?: "default" | "dashboard";
};

/**
 * Standard page padding wrapper. Replaces the padding AuthenticatedLayout used
 * to provide.
 *
 * Scrolls with `<main>` by default. A page that owns its own scroll regions
 * instead passes `h-full`: `<main>` is a flex item with a definite height, so
 * `100%` (and the `flex-1 min-h-0` chain below it) resolves against the real
 * box. Never size a page with `100dvh - var(--nav-height)` — that is only right
 * while the shell's chrome is exactly that tall, and overflows `<main>` by the
 * difference when a banner is up (SD-16854).
 */
export const PageContent: FC<PageContentProps> = memo(
  ({ children, className, variant = "default" }) => (
    <div
      className={cn(
        "flex-1 min-h-0 flex flex-col",
        variant === "dashboard"
          ? "dashboard-page"
          : "px-4 md:px-8 lg:px-12 pt-6 pb-5",
        className,
      )}
    >
      <div
        className={cn(
          "flex flex-1 min-h-0",
          variant === "dashboard" && "mx-auto w-full max-w-5xl",
        )}
      >
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">{children}</div>
      </div>
    </div>
  ),
);

PageContent.displayName = "PageContent";
